import React from 'react'
import { RouteComponentProps } from 'react-router-dom'
import {
  useGroupWithMembersData,
  useStateWithDebounce,
  useGroupsData,
  SortByType,
  useRemoveGroupMember,
} from '@modmd/data'
import { DEVICE } from 'theme'
import { useSortChangeHandler } from 'utils/helpers'
import { CsvInviteNewGroupMembers } from 'sharedComponents/CsvInviteNewGroupMembers'
import { AddNewGroupMembers } from 'sharedComponents/AddNewGroupMembers'
import { CompanyPageLayout } from 'client-portal/components/PageLayout'
import { ROUTES, COMPANY_ROUTES } from 'client-portal/constants/routes'
import TextInput from 'components/TextInput'
import { Details } from 'sharedComponents/Details'
import Icon from 'components/Icon'
import Label from 'components/Label'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import { useTranslation } from 'react-i18next'
import Datalist from 'components/Datalist'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Pagination from 'components/Pagination'
import Card from 'components/Card'
import Button from 'components/Button'
import { EditMemberRole } from 'sharedComponents/EditMemberRole'
import { EditGroupInfo } from 'sharedComponents/EditGroupInfo'
import { Box } from 'components/Layout'
import { QRCodeModal } from 'sharedComponents/QRCodeModal/QRCodeModal'

interface RouterProps {
  groupId: string
}

const SKELETON_DETAILS_COLUMNS = 4
const SKELETON_DETAILS_ROWS = 1

const SKELETON_MEMBERS_COLUMNS = 5
const PAGE_LENGTH = 5

const CompanyGroupDetails: React.VFC<RouteComponentProps<RouterProps>> = ({ match, history }) => {
  const { t } = useTranslation()
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const { groupId } = match.params

  const searchState = useSearchState<{
    page?: string
    emailOrNameLike?: string
  }>()
  const [emailOrNameLike, setEmailOrNameLike] = useStateWithDebounce(
    searchState.data.emailOrNameLike || '',
    (emailOrNameLike) => {
      searchState.setSearchState({ emailOrNameLike, page: 1 }, false, true)
    }
  )

  const [QRCodeDialog, setQRCodeDialog] = React.useState(false)

  const { sorter, handleSortChange } = useSortChangeHandler()
  const groupWithMembersQuery = useGroupWithMembersData({
    groupId,
    membersPage: Number(searchState.data.page || 1),
    filterData: {
      emailOrNameLike: searchState.data.emailOrNameLike ?? '',
      isDeleted: false,
    },
    pageLength: PAGE_LENGTH,
    membersSorter: sorter,
  })
  const membersPagination = groupWithMembersQuery.data?.group?.Members?.pagination || {
    total: 0,
    length: 1,
    from: 0,
  }

  const groupsQuery = useGroupsData({
    filter: {
      id: groupId,
    },
  })
  const {
    isRefetching: areMembersRefetching,
    isLoadingRemoveGroupMember,
    handleRemoveGroupMember,
  } = useRemoveGroupMember()
  const group = groupsQuery.data?.groups?.Groups[0]
  const companyId = group?.Company?.id || ''

  return (
    <CompanyPageLayout>
      <Details
        topContent={
          <Datalist
            isLoading={groupsQuery.loading}
            skeletonProps={{
              columns: SKELETON_DETAILS_COLUMNS,
              rows: SKELETON_DETAILS_ROWS,
            }}
            header={
              <Datalist.Title
                rightContent={
                  <Button
                    onClick={() =>
                      history.push(
                        `/${ROUTES.COMPANY}/${companyId}/${ROUTES.REQUEST_TEST_SESSION}/${groupId}`
                      )
                    }
                    leftIcon={<Icon.Plus size="L" />}
                  >
                    Schedule
                  </Button>
                }
              >
                {t('groupDetails')}
              </Datalist.Title>
            }
            data={[
              {
                id: group?.id,
                name: group?.name,
                company: group?.Company?.name,
                countOfMembers: group?.Members?.pagination.total,
              },
            ]}
            renderTableHeader={() => (
              <Datalist.Row display="grid" gridTemplateColumns="repeat(4, 1fr)">
                <Datalist.HeaderCell label="id" />
                <Datalist.HeaderCell label={`${t('name')}`} />
                <Datalist.HeaderCell label="company" />
                <Datalist.HeaderCell label="Count of Members" />
              </Datalist.Row>
            )}
            shouldRenderTableHeader={isDesktop}
            renderTableRow={({ id, name, company, countOfMembers }) => (
              <Datalist.Row
                display="grid"
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: 'repeat(4, 1fr)',
                }}
              >
                <Datalist.Cell label="id">{id}</Datalist.Cell>
                <Datalist.Cell label={`${t('name')}`}>
                  <EditGroupInfo
                    groupId={id as string}
                    initialName={name as string}
                    editIconSize="small"
                    refetchAfterEdit={() => groupWithMembersQuery.refetch()}
                  />
                  {name}
                </Datalist.Cell>
                <Datalist.Cell label="company">{company}</Datalist.Cell>
                <Datalist.Cell label="Number of Members">{countOfMembers}</Datalist.Cell>
              </Datalist.Row>
            )}
          />
        }
      >
        <Card width="100%">
          <Datalist
            isLoading={groupWithMembersQuery.loading || areMembersRefetching}
            skeletonProps={{
              columns: SKELETON_MEMBERS_COLUMNS,
              rows: PAGE_LENGTH,
            }}
            header={
              <Datalist.Title
                rightContent={
                  <>
                    <Button
                      style={{ marginRight: '1rem' }}
                      appearance="link"
                      onClick={() => setQRCodeDialog(true)}
                      leftIcon={<Icon.ExternalLinkOutline />}
                    >
                      QRCode
                    </Button>
                    <TextInput
                      value={emailOrNameLike}
                      placeholder="Search member"
                      onChange={(event) => setEmailOrNameLike(event.target.value)}
                      leftIcon={<Icon.Search />}
                      hasBottomContent={false}
                      hasTopContent={false}
                    />
                    <AddNewGroupMembers
                      companyId={companyId}
                      groupId={groupId}
                      onAddSuccess={() => groupWithMembersQuery.refetch()}
                    />
                    <CsvInviteNewGroupMembers
                      companyId={companyId}
                      groupId={groupId}
                      onUploadSuccess={() => groupWithMembersQuery.refetch()}
                    />
                  </>
                }
              >
                {t('groupMembers')}
              </Datalist.Title>
            }
            data={
              groupWithMembersQuery.data?.group?.Members?.Members.map(
                ({ User, role, acceptedAt }) => {
                  const { id, firstName, lastName, email, ParentUser } = User
                  return {
                    id,
                    name: `${(firstName as string) || ''} ${(lastName as string) || ''}`,
                    email,
                    acceptedAt,
                    role,
                    ParentUser,
                  }
                }
              ) || []
            }
            sorter={sorter}
            onSortChange={handleSortChange}
            renderTableHeader={() => (
              <Datalist.Row display="grid" gridTemplateColumns="repeat(6, 1fr)">
                <Datalist.HeaderCell label="id" />
                <Datalist.HeaderCell sortKey={SortByType.LAST_NAME} label={`${t('name')}`} />
                <Datalist.HeaderCell sortKey={SortByType.EMAIL} label="email" />
                <Datalist.HeaderCell label="role" />
                <Datalist.HeaderCell label="status" />
              </Datalist.Row>
            )}
            shouldRenderTableHeader={isDesktop}
            renderTableRow={({ id, name, email, role, acceptedAt, ParentUser }) => (
              <Datalist.Row
                onClick={() =>
                  history.push(`/${ROUTES.COMPANY}/${companyId}/${COMPANY_ROUTES.MEMBERS}/${id}`, {
                    from: COMPANY_ROUTES.GROUPS,
                  })
                }
                display="grid"
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: 'repeat(6, 1fr)',
                }}
              >
                <Datalist.Cell label="id">{id}</Datalist.Cell>
                <Datalist.Cell label={`${t('name')}`}>{name}</Datalist.Cell>
                <Datalist.Cell label="email">
                  {' '}
                  {email ?? (
                    <Box display="grid">
                      <span>{ParentUser?.email}</span>
                      <Label indicator="yellow">Subperson</Label>
                    </Box>
                  )}
                </Datalist.Cell>
                <Datalist.Cell label="role">
                  <EditMemberRole
                    userId={id}
                    companyOrGroupId={groupId}
                    memberReference="group"
                    initialRole={role}
                    refetchAfterEdit={groupWithMembersQuery.refetch}
                  />
                  {role}
                </Datalist.Cell>
                <Datalist.Cell label="status">
                  {!acceptedAt ? (
                    <Label indicator="blue">{`${t('requested')}`}</Label>
                  ) : (
                    <Label indicator="green">{`${t('accepted')}`}</Label>
                  )}
                </Datalist.Cell>
                <Datalist.Cell>
                  <Button
                    disabled={isLoadingRemoveGroupMember}
                    size="small"
                    colorVariant="danger"
                    appearance="ghost"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (window.confirm(`Are you sure you want to delete user ${name}?`)) {
                        void handleRemoveGroupMember({
                          userId: id,
                          groupId,
                          refetchQuery: groupWithMembersQuery.refetch,
                        })
                      }
                    }}
                  >
                    <Icon.Trash />
                  </Button>
                </Datalist.Cell>
              </Datalist.Row>
            )}
          />
        </Card>
        {membersPagination.total > membersPagination.length && (
          <Pagination.Wrapper>
            <Pagination
              firstPage={1}
              lastPage={Math.ceil(membersPagination.total / membersPagination.length)}
              currentPage={Math.ceil(membersPagination.from / membersPagination.length) + 1}
              onPageChange={(pageNumber) => {
                searchState.setSearchState({ page: pageNumber }, false)
              }}
            />
          </Pagination.Wrapper>
        )}
      </Details>
      <QRCodeModal
        isOpen={QRCodeDialog}
        onDismiss={() => setQRCodeDialog(false)}
        title={group?.name || ''}
        subTitle="Scan the following QRCODE to proceed to sign up to group"
        qrCodeValue={`${process.env.REACT_APP_PORTAL_CLIENT as string}/user-invitation/${groupId}`}
      />
    </CompanyPageLayout>
  )
}

export { CompanyGroupDetails }
